import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #788 — Facebook Page connector type seed.
//
// Distinct from `facebook-messenger` (migration 002): pages publishing
// (`social-posting` capability) and Messenger DMs (`messaging` capability)
// require different Meta App Review tracks and different OAuth scopes.
// Customers may want one without the other, so each gets its own connector
// type row + adapter registration.
const CAPABILITIES = JSON.stringify(["social-posting"] satisfies CapabilityName[]);

/**
 * Seed the `facebook-page` connector type row.
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration after the
 *   #788 follow-ups (video/Reel/Carousel) won't overwrite operator edits
 *   to the row (e.g. scope changes, icon swaps).
 * - Primary key `id` is set equal to `slug` so the capability registry can
 *   register under the SAME string used by `connector_instances.type_id` —
 *   keeps adapter lookup uniform across the dashboard, dispatch, and
 *   capability-call paths.
 * - `oauth_config.scopes` lists the full Facebook-first social connector
 *   scope set. Meta App Review must see each capability exercised:
 *     - `pages_show_list`       (listAccounts)
 *     - `pages_manage_posts`    (publish/update/schedule/delete)
 *     - `pages_read_engagement` (getInsights)
 *     - `pages_manage_metadata` (Page webhook subscription)
 *     - `pages_read_user_content` (read comments/user content)
 *     - `pages_manage_engagement` (create/update/delete comments)
 *     - `business_management`   (Business Portfolio / agency asset traversal)
 *   Adding scopes here requires re-submission to Meta App Review.
 * - `clientIdEnvFallbacks` / `clientSecretEnvFallbacks` let operators reuse
 *   the existing Messenger Meta App credentials when Page publishing uses
 *   the same Meta App. The Page-specific env names still override when set.
 * - `configIdEnv` lets operators opt into Facebook Login for Business; when
 *   the env var is set, `/oauth/start` sends `config_id` instead of `scope`.
 * - `webhook_config` records the Page feed webhook signature config. Actual
 *   delivery still depends on Meta App dashboard callback setup.
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenant-local
 *   time isn't available at seed time, and the column is audit-only.
 */
export const migration: ModuleMigrationDef = {
  key: "011_facebook_page_seed",
  version: "0.11.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'facebook-page',
  'facebook-page',
  'Facebook Page',
  'Facebook',
  NULL,
  '${CAPABILITIES}',
  '{"tokenUrl":"https://graph.facebook.com/v25.0/oauth/access_token","clientIdEnv":"FACEBOOK_PAGE_CLIENT_ID","clientIdEnvFallbacks":["FACEBOOK_MESSENGER_CLIENT_ID"],"clientSecretEnv":"FACEBOOK_PAGE_CLIENT_SECRET","clientSecretEnvFallbacks":["FACEBOOK_MESSENGER_CLIENT_SECRET","FB_APP_SECRET"],"authorizeUrl":"https://www.facebook.com/v25.0/dialog/oauth","scopes":["pages_show_list","pages_manage_posts","pages_read_engagement","pages_manage_metadata","pages_read_user_content","pages_manage_engagement","business_management"],"configIdEnv":"FACEBOOK_PAGE_CONFIG_ID"}',
  '{"signatureHeader":"x-hub-signature-256","algorithm":"HMAC-SHA256","secretEnv":"FB_APP_SECRET","events":["feed"]}',
  1,
  '2026-05-23T00:00:00Z',
  '2026-05-23T00:00:00Z'
);
  `.trim(),
};
